"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { TemplateCard } from "./TemplateCard";
import { CATEGORIES, TEMPLATES, coverageForTemplate, templateSearchText, type CategoryKey } from "@/lib/library";
import { track } from "@/lib/analytics";

function templateHaystack(t: (typeof TEMPLATES)[number]): string {
  return templateSearchText(t);
}

// Generic words that should never gate a match — every RAMS title contains them,
// so requiring them just suppresses good results without adding precision.
const SEARCH_STOPWORDS = new Set([
  "rams", "ram", "method", "statement", "risk", "assessment", "assessments",
  "operation", "operations", "work", "works", "working", "installation", "install",
  "the", "a", "of", "for", "and", "on", "to", "with",
]);

// Match if the template contains every meaningful token, tolerating ONE miss so a
// natural extra word (e.g. "mobile" on a crane lift, "pipe" on gas/plumbing) doesn't
// zero out an obvious result. Stopwords are stripped first; a match still requires at
// least one content token to hit, so we never return the whole library.
function matches(haystack: string, tokens: string[]): boolean {
  const content = tokens.filter((tok) => !SEARCH_STOPWORDS.has(tok));
  if (content.length === 0) return tokens.every((tok) => haystack.includes(tok));
  const hits = content.filter((tok) => haystack.includes(tok)).length;
  return hits >= 1 && hits >= content.length - 1;
}

export function TemplateLibrary({
  draftIds = [],
  initialLimit,
  cardsToBuilder = false,
}: {
  draftIds?: string[];
  /** Show only the first N cards until the user searches, filters or expands —
   *  for landing pages where the full grid is overwhelming. */
  initialLimit?: number;
  /** Live cards deep-link straight into the builder (paid landing pages). */
  cardsToBuilder?: boolean;
}) {
  const draftSet = useMemo(() => new Set(draftIds), [draftIds]);
  const [expanded, setExpanded] = useState(false);
  const [active, setActive] = useState<CategoryKey | "all">("all");
  const [query, setQuery] = useState("");
  const [tradeFilter, setTradeFilter] = useState("all");
  const [coverageFilter, setCoverageFilter] = useState("all");
  const [requestDetails, setRequestDetails] = useState("");
  const [requestEmail, setRequestEmail] = useState("");
  const [requestStatus, setRequestStatus] = useState<"idle" | "loading" | "done" | "error">("idle");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cat = params.get("cat") as CategoryKey | null;
    if (cat && CATEGORIES.some((c) => c.key === cat)) setActive(cat);
    const q = params.get("q") || params.get("search") || "";
    if (q.trim()) setQuery(q.trim());
  }, []);

  const q = query.trim().toLowerCase();
  const tokens = q ? q.split(/\s+/) : [];
  const searching = tokens.length > 0;
  const tradeOptions = useMemo(() => Array.from(new Set(TEMPLATES.map((t) => t.trade))).sort(), []);
  const coverageOptions = useMemo(
    () => Array.from(new Set(TEMPLATES.flatMap((t) => coverageForTemplate(t)))).sort(),
    [],
  );

  // When searching, match across the whole library (findability beats category scoping);
  // with no query, fall back to the category filter. Trade/coverage filters apply in both modes.
  const templates = useMemo(
    () =>
      TEMPLATES.filter((t) => {
        const categoryOk = searching || active === "all" || t.category === active;
        const tradeOk = tradeFilter === "all" || t.trade === tradeFilter;
        const coverageOk = coverageFilter === "all" || coverageForTemplate(t).includes(coverageFilter);
        const queryOk = !searching || matches(templateHaystack(t), tokens);
        return categoryOk && tradeOk && coverageOk && queryOk;
      }),
    [active, searching, q, tradeFilter, coverageFilter],
  );
  const resultCount = templates.length;

  // The limit only bites the untouched default view — any search, filter or
  // category choice shows everything, so findability is never capped.
  const limited =
    !!initialLimit &&
    !expanded &&
    !searching &&
    active === "all" &&
    tradeFilter === "all" &&
    coverageFilter === "all" &&
    resultCount > initialLimit;
  const shownTemplates = limited ? templates.slice(0, initialLimit) : templates;

  // Tracks the last settled, results-bearing search so we can detect "searched, saw
  // matches, picked none" — the coverage-vs-relevance signal (distinct from zero-result).
  const pending = useRef<{ query: string; results: number; clicked: boolean } | null>(null);
  const flushNoClick = () => {
    const p = pending.current;
    if (p && p.results > 0 && !p.clicked) track("template_search_no_click", { query: p.query, results: p.results });
  };

  // Debounced search-intent capture — what people look for, what returns nothing (gold),
  // and (now) what returns results they don't click.
  useEffect(() => {
    if (!searching || q.length < 2) return;
    const id = setTimeout(() => {
      if (pending.current && pending.current.query !== q) flushNoClick();
      const props = { query: q, results: resultCount, trade: tradeFilter, coverage: coverageFilter };
      track("template_search", props);
      track("template_search_performed", props);
      if (resultCount === 0) {
        track("template_search_no_results", props);
        track("template_search_zero_results", props);
      }
      pending.current = { query: q, results: resultCount, clicked: false };
    }, 650);
    return () => clearTimeout(id);
  }, [q, searching, resultCount, tradeFilter, coverageFilter]);

  // Flush a no-click when the user leaves the library (SPA nav / unmount).
  useEffect(() => () => flushNoClick(), []);

  async function submitRequest(e: React.FormEvent) {
    e.preventDefault();
    const details = requestDetails.trim() || query.trim();
    if (!details || requestStatus === "loading") return;
    setRequestStatus("loading");
    try {
      const res = await fetch("/api/event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: window.location.hostname.replace(/^www\./, ""),
          session_id: crypto.randomUUID(),
          event_type: "report_request",
          page: "/templates/",
          data: JSON.stringify({
            query: query.trim(),
            details: details.substring(0, 500),
            email: requestEmail.trim().toLowerCase(),
            category: active,
            trade: tradeFilter,
            coverage: coverageFilter,
            zeroResult: resultCount === 0,
          }),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      track("template_request_submitted", {
        query: query.trim(),
        has_email: !!requestEmail.trim(),
        zero_results: resultCount === 0,
        trade: tradeFilter,
        coverage: coverageFilter,
      });
      setRequestStatus("done");
    } catch {
      setRequestStatus("error");
    }
  }

  return (
    <div>
      <div className="mb-6">
        <form action="/templates/" method="get" className="grid gap-3 lg:grid-cols-[1fr_auto_auto]">
          <input
            type="search"
            name="q"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setRequestStatus("idle");
            }}
            placeholder="Search by job — e.g. boiler install, excavation near services, hot works welding"
            aria-label="Search RAMS templates"
            className="w-full rounded-xl border border-line bg-white px-4 py-3 text-base text-ink focus:border-emerald focus:outline-none"
          />
          <select
            value={tradeFilter}
            onChange={(e) => {
              setTradeFilter(e.target.value);
              track("template_filter_changed", { filter: "trade", value: e.target.value });
            }}
            aria-label="Filter by trade"
            className="rounded-xl border border-line bg-white px-4 py-3 text-sm font-medium text-ink focus:border-emerald focus:outline-none"
          >
            <option value="all">All trades</option>
            {tradeOptions.map((trade) => (
              <option key={trade} value={trade}>{trade}</option>
            ))}
          </select>
          <select
            value={coverageFilter}
            onChange={(e) => {
              setCoverageFilter(e.target.value);
              track("template_filter_changed", { filter: "coverage", value: e.target.value });
            }}
            aria-label="Filter by RAMS coverage"
            className="rounded-xl border border-line bg-white px-4 py-3 text-sm font-medium text-ink focus:border-emerald focus:outline-none"
          >
            <option value="all">All risk types</option>
            {coverageOptions.map((coverage) => (
              <option key={coverage} value={coverage}>{coverage}</option>
            ))}
          </select>
        </form>
      </div>

      {!searching && (
        <div className="flex flex-wrap gap-2">
          <Pill
            label="All"
            count={TEMPLATES.length}
              on={active === "all"}
              onClick={() => {
                setActive("all");
                track("template_category_selected", { category: "all" });
                track("template_filter_changed", { filter: "category", value: "all" });
              }}
            />
          {CATEGORIES.map((c) => (
            <Pill
              key={c.key}
              label={c.label}
              count={TEMPLATES.filter((t) => t.category === c.key).length}
              on={active === c.key}
              onClick={() => {
                setActive(c.key);
                track("template_category_selected", { category: c.key });
                track("template_filter_changed", { filter: "category", value: c.key });
              }}
            />
          ))}
        </div>
      )}

      {searching && (
        <p className="text-sm text-body">
          {resultCount} result{resultCount === 1 ? "" : "s"} for &ldquo;{query.trim()}&rdquo;
        </p>
      )}

      {searching && resultCount === 0 ? (
        <div className="mt-8 rounded-xl border border-line bg-white p-6 shadow-soft">
          <div className="max-w-2xl">
            <p className="font-display text-lg font-bold text-ink">No RAMS match &ldquo;{query.trim()}&rdquo; yet.</p>
            <p className="mt-1 text-sm leading-relaxed text-body">
              Tell us the exact trade or task. Zero-result requests directly decide what we add to the library next.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a href="/builder/" className="inline-flex rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink-2">
                Try the RAMS builder
              </a>
              <a href="/tools/rams-checker/" className="inline-flex rounded-lg border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink">
                Check an existing RAMS
              </a>
            </div>
          </div>
          {requestStatus === "done" ? (
            <div className="mt-4 rounded-xl border border-emerald/30 bg-emerald-tint p-4 text-sm text-ink">
              Request received. We build the most-requested RAMS first{requestEmail.trim() ? " and will email you when this one is ready" : ""}.
            </div>
          ) : (
            <form onSubmit={submitRequest} className="mt-4 grid gap-3 md:grid-cols-[1fr_240px_auto]">
              <textarea
                rows={2}
                value={requestDetails}
                onChange={(e) => setRequestDetails(e.target.value)}
                maxLength={500}
                placeholder={`Describe the RAMS you need, e.g. ${query.trim() || "boiler flue through roof on occupied premises"}`}
                aria-label="Describe the RAMS you need"
                className="rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink focus:border-emerald focus:outline-none md:row-span-2"
              />
              <input
                type="email"
                value={requestEmail}
                onChange={(e) => setRequestEmail(e.target.value)}
                placeholder="Email for update (optional)"
                aria-label="Email for update (optional)"
                className="rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink focus:border-emerald focus:outline-none"
              />
              <button
                type="submit"
                disabled={requestStatus === "loading"}
                className="rounded-full bg-emerald px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-deep disabled:opacity-60"
              >
                {requestStatus === "loading" ? "Sending..." : "Request this RAMS"}
              </button>
              {requestStatus === "error" && (
                <p role="alert" className="text-sm font-semibold text-risk-veryhigh md:col-span-2">Something went wrong — try again or email hello@ramsdocs.co.uk.</p>
              )}
            </form>
          )}
        </div>
      ) : (
        <div
          className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          onClickCapture={(e) => {
            const a = (e.target as HTMLElement).closest("a");
            if (a) {
              const href = a.getAttribute("href") || "";
              if (searching && pending.current) pending.current.clicked = true;
              if (searching) track("template_result_clicked", { query: q, href });
              track("template_card_clicked", { query: q, href, category: active, trade: tradeFilter, coverage: coverageFilter });
            }
          }}
        >
          {shownTemplates.map((t) => (
            <TemplateCard key={t.id} t={t} draft={draftSet.has(t.id)} toBuilder={cardsToBuilder} />
          ))}
        </div>
      )}
      {limited && (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => {
              setExpanded(true);
              track("template_filter_changed", { filter: "limit", value: "show-all" });
            }}
            className="rounded-full border border-line-2 px-6 py-3 text-sm font-semibold text-ink transition-colors hover:border-ink hover:bg-mist"
          >
            Show all {resultCount} templates
          </button>
        </div>
      )}
    </div>
  );
}

function Pill({ label, count, on, onClick }: { label: string; count: number; on: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
        on ? "border-emerald bg-emerald text-white" : "border-line bg-white text-body hover:border-line-2 hover:text-ink"
      }`}
    >
      {label} <span className="opacity-60">{count}</span>
    </button>
  );
}
